// 原系統對話框的**規則層**（資料層是 legacy-modals.generated.ts，由
// scripts/extract-legacy-modals.ts 從 legacy raw DOM 產生，不手改）。
//
// 分工與 legacy-fields.ts / legacy-fields.generated.ts 那一組完全相同：那邊是資料，這邊是規則。
//
// ---------------------------------------------------------------------------
// 這一層做四件事
// ---------------------------------------------------------------------------
// 1. **三級分群的取用介面**。FUNCTIONAL-CONTRACT.md:106「global 與 shared 各做一次全站受惠；
//    unique 逐頁做」——所以 global 掛在 AppLayout、shared 掛在 src/features/dialogs、
//    unique 由頁面模板逐頁畫。同一個 `key` 在多頁出現時只實作一次。
//
// 2. **排除 CSRF**（沿用 FC-3 Q2 裁決與 legacy-fields.ts 的第 2 點）。
//    雛形沒有 session、沒有 CSRF 機制，畫一個永遠空值、送出時也沒有人驗的 hidden input
//    就是 criteria C5-e 定義的假欄位。這些項在 C5-d 的分母外，歸 (e) 並附固定 todo。
//    **不影響 signatureNoId**：`csrfToken` 是 hidden，而 E-Q2 裁決已把 hidden 排除在 key 外。
//
// 3. **提供 select 的可選項**（作法逐字沿用 legacy-fields.ts 的第 3 點）：
//    static 列舉直接用抽到的列舉代碼；dynamic（外鍵）下拉在原系統是資料庫內容，
//    功能契約明寫這種 options「只比存在與 multiple」，所以改用雛形自己的假資料層。
//
// 4. **指定每個對話框的「關閉鈕」與「送出鈕」**（見下一段），這是 C5-d 接線與
//    C8-a′ 互動擷取「關得掉」的前提。
//
// ---------------------------------------------------------------------------
// 關閉鈕／送出鈕怎麼挑（不是隨便挑一顆）
// ---------------------------------------------------------------------------
// 對話框的按鈕文字**逐字來自 legacy raw DOM**（signatureNoId 的組成之一），
// 所以不能為了「好操作」而多畫或改字。本層只是從既有按鈕裡**指派角色**：
//   - 關閉鈕 = 第一顆文字命中 CLOSE_TEXTS 的按鈕（`×`／`×Close`／`關閉`／`取消`／`Close`）。
//   - 送出鈕 = **最後一顆**不命中 CLOSE_TEXTS 的按鈕（legacy 的存檔／送出型鈕一律排在最後）。
//   - 兩者都可能是同一顆：實測 `#syncPeopleModal` 只有一顆「確定」
//     （`src/ui/.tmp/ui/raw-dom/legacy/admin/config/config-account-v-2.html` 那一群），
//     它在原系統就是「按下去＝送出並關閉」，所以本層把它同時指派為送出與關閉。
//   - 一顆都挑不到關閉鈕時退回送出鈕；兩者都沒有時對話框只能用 Esc 關
//     （`LegacyContractModal` 有 Esc 處理），並且會被 `modalsWithoutCloseAffordance()` 列出來。

import { buildValues, type Column } from '@/mocks/fixtures';
import {
  LEGACY_MODALS,
  type LegacyModalField,
  type LegacyModalOption,
  type LegacyModalSpec,
} from '@/mocks/legacy-modals.generated';

export type { LegacyModalField, LegacyModalOption, LegacyModalSpec };

/** 沿用 legacy-fields.ts 的 CSRF 判準（雛形沒有 session，畫了就是假欄位）。 */
const CSRF_NAME = /^(?:csrf(?:Token)?|_csrf|anti-?csrf(?:-?token)?)$/i;

/** 關閉型按鈕的文字（逐字比對 legacy 抽到的按鈕文字，不做同義詞擴張）。 */
export const CLOSE_TEXTS: ReadonlySet<string> = new Set(['×', '×Close', 'Close', '關閉', '取消']);

/** 這個欄位要不要畫（CSRF 一律不畫）。 */
export function isRenderableModalField(field: LegacyModalField): boolean {
  return !CSRF_NAME.test(field.name) && field.name !== '';
}

/** 被排除的 CSRF 欄位（criteria C5-d 的排除條款要逐筆列出）。 */
export function excludedCsrfFields(spec: LegacyModalSpec): readonly LegacyModalField[] {
  return spec.fields.filter((f) => CSRF_NAME.test(f.name));
}

/** 這個對話框實際要畫的欄位。 */
export function modalFieldsToRender(spec: LegacyModalSpec): readonly LegacyModalField[] {
  return spec.fields.filter(isRenderableModalField);
}

/**
 * pageId → 該頁要畫的對話框（資料層已只含 trigger tier A–D）。
 * `level` 給定時只回傳該級別的。
 */
export function legacyModalsFor(
  pageId: string,
  level?: LegacyModalSpec['level'],
): readonly LegacyModalSpec[] {
  const list = LEGACY_MODALS[pageId] ?? [];
  const filtered = level === undefined ? list : list.filter((s) => s.level === level);
  // 同一頁同一個 key 只畫一次（legacy 同頁重複出現同內容的對話框時，
  // signatureNoId 是集合語意，畫兩次不會多得分，只會多一顆 trigger）。
  const seen = new Set<string>();
  return filtered.filter((s) => {
    if (seen.has(s.key)) return false;
    seen.add(s.key);
    return true;
  });
}

/** 全站唯一的 global 對話框規格（`#changeProfileModal`，117 頁共用）。 */
export function globalLegacyModals(): readonly LegacyModalSpec[] {
  const seen = new Set<string>();
  const out: LegacyModalSpec[] = [];
  for (const list of Object.values(LEGACY_MODALS)) {
    for (const spec of list) {
      if (spec.level !== 'global' || seen.has(spec.key)) continue;
      seen.add(spec.key);
      out.push(spec);
    }
  }
  return out;
}

/** 全站的 shared 對話框規格（2–107 頁出現者），去重後一次列出。 */
export function sharedLegacyModals(): readonly LegacyModalSpec[] {
  const seen = new Set<string>();
  const out: LegacyModalSpec[] = [];
  for (const list of Object.values(LEGACY_MODALS)) {
    for (const spec of list) {
      if (spec.level !== 'shared' || seen.has(spec.key)) continue;
      seen.add(spec.key);
      out.push(spec);
    }
  }
  return out;
}

const dynamicCache = new Map<string, readonly LegacyModalOption[]>();

/**
 * select 的可選項。static 用抽到的列舉代碼（欄位定義的一部分）；
 * dynamic 用雛形自己的假資料層——原系統那些選項是資料庫內容，
 * 功能契約對 dynamic options「只比存在與 multiple」，值不參與比對。
 */
export function modalOptionsFor(
  spec: LegacyModalSpec,
  field: LegacyModalField,
): readonly LegacyModalOption[] {
  if (field.optionsKind === 'static') return field.options ?? [];
  if (field.optionsKind !== 'dynamic') return [];
  const cacheKey = `${spec.key}¦${field.name}`;
  const hit = dynamicCache.get(cacheKey);
  if (hit !== undefined) return hit;
  // 作法逐字沿用 legacy-fields.ts:188-202：同一個 (key, name) 永遠得到同一組確定性假資料。
  const column: Column = {
    key: 'value',
    label: field.label === '' ? field.name : field.label,
    kind: 'text',
  };
  const options: LegacyModalOption[] = [];
  for (let index = 0; index < 3; index += 1) {
    const value = buildValues(`modal:${spec.key}#${field.name}`, index, [column]).value;
    const text = value === undefined || value === null ? '' : String(value);
    if (text !== '' && !options.some((o) => o.value === text)) options.push({ value: text, text });
  }
  dynamicCache.set(cacheKey, options);
  return options;
}

/**
 * 這個控制項有沒有「使用者改值」這個事件（判準逐字沿用 legacy-fields.ts:255-261）。
 * hidden 與「沒有任何非空選項的 select」沒有 —— 它們的接線由「值一定進 payload」保證。
 */
export function isModalFieldUserDrivable(spec: LegacyModalSpec, field: LegacyModalField): boolean {
  if (field.hidden) return false;
  if (field.type === 'select' || field.type === 'select-multiple') {
    return modalOptionsFor(spec, field).some((o) => o.value !== '');
  }
  return true;
}

/** 日期樣式（沿用 legacy-fields.ts 的 DATE_LIKE 判準）。 */
const DATE_LIKE = /^\d{4}[-/]\d{1,2}[-/]\d{1,2}/;
const SENTINEL_VALUES: ReadonlySet<string> = new Set(['', '0', '-1']);
const FOREIGN_KEY_NAME = /id\d*$|^(annual|organization|formDepartment|formTemplate)\d*$/i;

/** hidden 欄位的初始值：只帶「與資料列無關的常數參數」（理由見 legacy-fields.ts 檔頭）。 */
export function modalSeedValue(field: LegacyModalField): string {
  if (!field.hidden) return '';
  const value = field.value ?? '';
  if (value === '') return '';
  if (DATE_LIKE.test(value)) return '';
  if (FOREIGN_KEY_NAME.test(field.name) && !SENTINEL_VALUES.has(value)) return '';
  return value;
}

export function modalInitialValues(spec: LegacyModalSpec): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of modalFieldsToRender(spec)) values[field.name] = modalSeedValue(field);
  return values;
}

/**
 * 送出的 payload。
 * 對**不可驅動**的欄位一律帶出 key（即使值是空字串）；可驅動的欄位維持「空的就不帶」——
 * 後者是 legacy-fields.ts:49-50 所記的那條變異（拿掉 onChange）能變紅的關鍵。
 * （R6 更正：原本這裡寫「C6 變異 4」、被引用的 legacy-fields.ts:49-50 寫「C6 變異 2」，
 *  兩份文件對同一條變異給了互斥的編號；改成直接指向那一行，不再自行編號。）
 */
export function modalToPayload(
  spec: LegacyModalSpec,
  values: Readonly<Record<string, string>>,
): Record<string, string> {
  const payload: Record<string, string> = {};
  for (const field of modalFieldsToRender(spec)) {
    const value = values[field.name] ?? '';
    if (!isModalFieldUserDrivable(spec, field) || value !== '') payload[field.name] = value;
  }
  return payload;
}

/** 關閉鈕的索引；找不到時回傳送出鈕的索引；都沒有回 -1。 */
export function modalCloseButtonIndex(spec: LegacyModalSpec): number {
  const i = spec.buttons.findIndex((b) => CLOSE_TEXTS.has(b.text));
  if (i !== -1) return i;
  return modalSubmitButtonIndex(spec);
}

/** 送出鈕的索引 = 最後一顆非關閉型按鈕；沒有回 -1。 */
export function modalSubmitButtonIndex(spec: LegacyModalSpec): number {
  for (let i = spec.buttons.length - 1; i >= 0; i -= 1) {
    const b = spec.buttons[i];
    if (b !== undefined && !CLOSE_TEXTS.has(b.text)) return i;
  }
  return -1;
}

/**
 * 一顆按鈕都指派不出「關閉」角色的對話框（只能靠 Esc 關）。
 * criteria C8-a′「關不掉」要逐頁列出，這個函式就是那份清單的來源。
 */
export function modalsWithoutCloseAffordance(): readonly { pageId: string; key: string }[] {
  const out: { pageId: string; key: string }[] = [];
  for (const [pageId, list] of Object.entries(LEGACY_MODALS)) {
    for (const spec of list) {
      if (modalCloseButtonIndex(spec) === -1) out.push({ pageId, key: spec.key });
    }
  }
  return out;
}

/** 這個對話框在 React 側的 `data-ui-id`（也是 `data-modal-trigger` 的值）。 */
export function modalUiId(pageId: string, spec: LegacyModalSpec): string {
  return `${pageId}-legacy-modal-${spec.key}`;
}

/** trigger 鈕的可見文字：legacy trigger 的文字，空的時候退回對話框標題／id。 */
export function modalTriggerLabel(spec: LegacyModalSpec): string {
  const fromTrigger = (spec.trigger?.label ?? '').trim();
  if (fromTrigger !== '') return fromTrigger;
  if (spec.title !== '') return spec.title;
  return spec.idHint ?? spec.key;
}
